import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { toast } from "sonner";
import api from "../lib/api";
import CashTenderDialog from "./retail/admin/CashTenderDialog";

// Dashboard-wide cash-tender confirmation (Sep 2026).
//
// ONE OWNER. This is the single controller for the cash-tender dialog
// anywhere under `/dashboard/*`: it owns the pending-cash queue, the
// polling, the per-session dismissals, the confirmation call, and the
// ONE `CashTenderDialog` that is ever mounted.
//
// The Retail Orders "Collect Cash" button used to open a SECOND
// `CashTenderDialog` of its own, so two could be mounted at once, each
// with its own document-level key handler and its own idea of the queue.
// One Escape reached both. It now calls `requestCashTender(orderId)` on
// this controller instead, which brings that order to the front of the
// SAME queue — one dialog, one owner, one source of truth for what is
// outstanding.
//
// WHERE IT APPEARS — and just as importantly, where it does not.
// This is mounted inside `DashboardLayout`, which is the authenticated
// TENANT dashboard shell and only that. Every `/dashboard/*` page renders
// through it, so a cash order raises the popup whichever dashboard screen
// staff happen to be on. The surfaces that must NOT show it are excluded
// structurally rather than by a list of route checks, because none of
// them renders through this shell at all:
//
//   * the customer storefront (`/retail/:slug`) — its own page tree;
//   * the public order-status screen (`/retail/:slug/orders/:code`);
//   * the Kitchen Display (`/kitchen/:slug`) — a standalone full-screen
//     page with its own chrome, deliberately not a dashboard shell;
//   * Internal Admin (`/internal-admin/*`) — its own protected layout.
//
// Tenant isolation is the backend's: `GET /retail/orders/pending-cash`
// pins `company_id` in the query itself, so this can only ever see — and
// only ever confirm — its own tenant's orders.
//
// DELIVERY. It reuses ordinary dashboard polling rather than introducing
// a realtime subsystem for one feature: one small, tenant-scoped GET on
// an interval, paused while the tab is hidden.

const POLL_MS = 20000;
// One page is what the popup needs to show the next order; the queue is
// paged so nothing beyond it is unreachable (see `fetchAllPending`).
const PAGE_SIZE = 50;
// A hard ceiling on how many pages one refresh will walk, so a very
// large backlog can never turn a background poll into a request storm.
const MAX_PAGES = 10;
// Dismissals are per browser session (not per device, not persisted to
// the server): a staff member who closes the popup is not nagged again
// for that order on that screen, while the order itself stays fully
// recoverable from Retail Orders — and comes back on a fresh session.
const DISMISSED_KEY = "rudras.cash_tender.dismissed.v1";

function readDismissed() {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.sessionStorage.getItem(DISMISSED_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(list) ? list : []);
  } catch (_e) {
    return new Set();
  }
}

function writeDismissed(set) {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(Array.from(set)));
  } catch (_e) {
    /* private mode / storage disabled — the popup simply reappears */
  }
}

// Walk the server's keyset cursor to the end, so every eligible order is
// in the queue — the newest included. A plain single request returned
// only the OLDEST page, which meant the newest pending cash order was
// unreachable from the recovery path once the backlog passed one page.
// Bounded by MAX_PAGES so this stays a cheap background poll.
async function fetchAllPending() {
  const all = [];
  const seen = new Set();
  let cursor = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const params = { limit: PAGE_SIZE };
    if (cursor) params.cursor = cursor;
    // eslint-disable-next-line no-await-in-loop
    const { data } = await api.get("/retail/orders/pending-cash", { params });
    const orders = Array.isArray(data?.orders) ? data.orders : [];
    orders.forEach((o) => {
      // The cursor is a strict keyset, so a duplicate should be
      // impossible; de-duplicating anyway means a server-side change
      // could never show staff the same order twice in one queue.
      if (o && o.id && !seen.has(o.id)) { seen.add(o.id); all.push(o); }
    });
    if (!data?.has_more || !data?.next_cursor) break;
    cursor = data.next_cursor;
  }
  return all;
}

const CashTenderContext = createContext(null);

/** Access the one dashboard-wide cash-tender controller. */
export function useCashTender() {
  return useContext(CashTenderContext);
}

export default function CashTenderWatcher({ enabled = true, children = null }) {
  const [queue, setQueue] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  // Lazy initialiser: `useRef(readDismissed())` re-read sessionStorage on
  // EVERY render and threw the result away after the first. This reads it
  // once, on mount.
  const dismissedRef = useRef(null);
  if (dismissedRef.current === null) dismissedRef.current = readDismissed();
  // An order the operator explicitly asked for from Retail Orders. It
  // jumps the queue and ignores its own dismissal, because the request
  // IS the undismissal.
  const [requestedId, setRequestedId] = useState(null);
  const aliveRef = useRef(true);
  // Double-submit guard (carried over from the Retail Orders page, which
  // owned the confirmation before this controller did). A ref, not the
  // `confirming` state: two clicks in the same tick both read the
  // pre-update state value and would both pass a state-based check,
  // producing two PATCHes for one gesture. The dialog disables its own
  // button as well; this is the one that cannot be raced.
  const submitRef = useRef(false);
  // Holds the IN-FLIGHT refresh promise, not a boolean. A concurrent
  // caller shares that promise instead of being turned away: `refresh`
  // used to return an empty list while a poll was running, and
  // `requestCashTender` reads an empty list as "this order is no longer
  // collectable" — so a Collect Cash click that happened to land during
  // the 20-second background poll told an operator a perfectly good
  // order was gone.
  const inFlightRef = useRef(null);
  // Pages that render lists of orders subscribe here so they can refresh
  // after a collection. The confirmation moved into this controller, so
  // without this the Retail Orders row that launched it would keep
  // showing "Collect Cash" until the operator reloaded the page.
  const collectedListenersRef = useRef(new Set());

  const refresh = useCallback(async () => {
    if (!enabled) return [];
    // A poll already running IS the refresh this caller wants. Share its
    // result rather than returning nothing — still exactly one request
    // in flight, and a caller that needs an answer now gets the real one.
    if (inFlightRef.current) return inFlightRef.current;
    const run = (async () => {
      try {
        const orders = await fetchAllPending();
        if (!aliveRef.current) return orders;
        // Oldest first is the server's ordering; it is preserved verbatim
        // so the customer who has been waiting longest is served first.
        setQueue(orders);
        // An order that was confirmed or cancelled elsewhere drops off the
        // server's list — forget its dismissal so the set cannot grow
        // without bound across a long shift.
        const live = new Set(orders.map((o) => o.id));
        let changed = false;
        dismissedRef.current.forEach((id) => {
          if (!live.has(id)) { dismissedRef.current.delete(id); changed = true; }
        });
        if (changed) writeDismissed(dismissedRef.current);
        return orders;
      } catch (_e) {
        // Retail not enabled for this tenant, a transient failure, or a
        // 401 already handled by the api interceptor. Never surface a
        // toast for a background poll.
        if (aliveRef.current) setQueue([]);
        return [];
      }
    })();
    inFlightRef.current = run;
    try {
      return await run;
    } finally {
      inFlightRef.current = null;
    }
  }, [enabled]);

  useEffect(() => {
    aliveRef.current = true;
    if (!enabled) return undefined;
    refresh();
    const timer = window.setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      refresh();
    }, POLL_MS);
    const onVisible = () => { if (!document.hidden) refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      aliveRef.current = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, refresh]);

  const pending = queue.filter(
    (o) => o.id === requestedId || !dismissedRef.current.has(o.id),
  );
  // An explicitly requested order is served first; otherwise oldest-first.
  const requested = requestedId ? pending.find((o) => o.id === requestedId) : null;
  const active = requested || pending[0] || null;

  // Keyed on the ACTIVE ORDER, not on mount: when the queue advances to a
  // different order in the same mounted dialog, the previous order's
  // error has to clear with it.
  useEffect(() => { setError(""); }, [active?.id]);

  const dismiss = () => {
    if (!active) return;
    // A dismissal is EXACTLY that: nothing is sent, nothing is marked
    // paid, and the order stays in the tenant's Retail Orders list with
    // its Collect Cash action intact.
    dismissedRef.current.add(active.id);
    writeDismissed(dismissedRef.current);
    if (active.id === requestedId) setRequestedId(null);
    setQueue((q) => [...q]);
  };

  const confirm = async (tenderedCents) => {
    if (!active || confirming) return;
    if (submitRef.current) return;
    // Read every field off the active order ONCE, here. Nothing below
    // reads `queue` or any other value the 20-second poll can move, so a
    // refresh landing mid-confirmation cannot re-point this at a
    // different order.
    const target = active;
    // Fail closed: no confirmation without an established amount due, and
    // none with a tender that does not cover it. The dialog enforces both
    // too; enforcing them here means no future caller can slip past.
    const dueCents = Number(target.amount_due_cents);
    if (!Number.isFinite(dueCents)) return;
    if (!Number.isFinite(Number(tenderedCents))
        || Number(tenderedCents) < dueCents) return;
    submitRef.current = true;
    setConfirming(true);
    setError("");
    try {
      const { data } = await api.patch(
        `/retail/orders/${target.id}/mark-cash-received`,
        {
          // The amount this dialog DISPLAYED, echoed back so the server
          // refuses the transition if it is no longer what is due
          // (`cash_receipt_amount_mismatch`). A queue entry that went
          // stale between the poll and the click cannot convert a wrong
          // figure into a `paid` stamp.
          expected_amount_cents: dueCents,
          cash_tendered_cents: Math.round(Number(tenderedCents)),
        },
      );
      toast.success(
        `Cash received for ${target.order_code || "order"}`
        + (data?.cash_change_due_cents
          ? ` · change ${(data.cash_change_due_cents / 100).toFixed(2)}`
          : ""),
      );
      // Drop it locally straight away so the next order in the queue
      // takes over without waiting for the next poll, then reconcile
      // with the server, which remains the authority on what is left.
      if (target.id === requestedId) setRequestedId(null);
      setQueue((q) => q.filter((o) => o.id !== target.id));
      collectedListenersRef.current.forEach((fn) => {
        try { fn(target.id); } catch (_err) { /* a listener must never break the till */ }
      });
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object" && detail.message)
        ? detail.message
        : (typeof detail === "string" ? detail : "Could not record cash payment");
      // A losing race (another member of staff confirmed first) is not a
      // failure the till needs to fight: the message says what happened
      // and the refresh below removes the order from this queue.
      setError(msg);
      refresh();
    } finally {
      submitRef.current = false;
      setConfirming(false);
    }
  };

  /**
   * Bring one order to the front of the shared queue and open the single
   * dialog on it. Used by the Retail Orders recovery button instead of
   * mounting a competing dialog.
   *
   * Returns true when the order is genuinely still collectable. The
   * authoritative amount due and quick amounts always come from the
   * server's own queue, never from a row rendered in a stale list.
   */
  const requestCashTender = useCallback(async (orderId) => {
    if (!orderId) return false;
    const orders = await refresh();
    const match = orders.find((o) => o.id === orderId);
    if (!match) return false;
    // Asking for it explicitly undoes an earlier dismissal of it.
    if (dismissedRef.current.has(orderId)) {
      dismissedRef.current.delete(orderId);
      writeDismissed(dismissedRef.current);
    }
    setRequestedId(orderId);
    return true;
  }, [refresh]);

  /** Be told when an order is collected. Returns an unsubscribe. */
  const onCollected = useCallback((fn) => {
    if (typeof fn !== "function") return () => {};
    const set = collectedListenersRef.current;
    set.add(fn);
    return () => set.delete(fn);
  }, []);

  const value = useMemo(
    () => ({ requestCashTender, refresh, onCollected, confirming }),
    [requestCashTender, refresh, onCollected, confirming],
  );

  return (
    <CashTenderContext.Provider value={value}>
      {children}
      {active ? (
        <CashTenderDialog
          order={active}
          onClose={dismiss}
          onConfirm={confirm}
          confirming={confirming}
          error={error}
          queueRemaining={Math.max(0, pending.length - 1)}
        />
      ) : null}
    </CashTenderContext.Provider>
  );
}
